import React from 'react';
import PropTypes from 'prop-types';

/**
 * BarChart component for horizontal bar charts
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of objects with name, value, and optional formattedValue and color
 * @param {string} props.color - Default bar color if useCustomColors is false
 * @param {string} props.dataLabel - Label for the data series
 * @param {boolean} props.useCustomColors - Whether to use colors from data objects
 * @param {number} props.barHeight - Height of each bar in pixels
 * @param {boolean} props.compact - Whether to use a more compact layout
 * @returns {JSX.Element} Rendered component
 */
const BarChart = (props) => { 
  const {
    data,
    color = '#3B82F6',
    dataLabel = 'Value',
    useCustomColors = false,
    barHeight = 24,
    compact = false,
    // Keep electorate for backward compatibility
    electorate,
    // Omit similarElectorates using object rest pattern
    ...rest
  } = props;
  
  // If we have the old props format, return a message to update
  if (!data && electorate) {
    return (
      <div className="p-4 text-center text-gray-500">
        Please update BarChart component with new data format
      </div>
    );
  }
  
  // Handle no data case
  if (!data || data.length === 0) {
    return <div className="p-4 text-center text-gray-600 font-medium">No data available</div>;
  }
  
  // Sort by value (descending)
  const sortedData = [...data].sort((a, b) => b.value - a.value);
  
  // Find max value for scaling
  const maxValue = Math.max(...sortedData.map(d => d.value));
  
  // For compact mode (especially for comparison of just 2 items)
  if (compact && sortedData.length <= 3) {
    return (
      <div className="h-full w-full flex flex-col justify-center">
        {sortedData.map((item, index) => (
          <div key={item.name} className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <div className="text-sm font-medium text-gray-700">{item.name}</div>
              <div className="text-sm font-medium text-gray-900">
                {item.formattedValue || item.value.toLocaleString()}
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded h-3">
              <div 
                className="rounded h-3"
                style={{ 
                  width: `${Math.max((item.value / maxValue) * 100, 3)}%`,
                  backgroundColor: useCustomColors && item.color ? item.color : color,
                }}
              ></div>
            </div>
          </div>
        ))}
        <div className="text-xs text-gray-500 text-right mt-2">{dataLabel}</div>
      </div>
    );
  }
  
  // Standard layout
  return (
    <div className="h-full w-full flex flex-col justify-center">
      {sortedData.map((item, index) => (
        <div key={item.name} className="flex items-center mb-4">
          <div className="w-1/4 text-sm truncate pr-2 text-gray-700">{item.name}</div>
          <div className="w-3/4 flex items-center">
            <div className="w-full bg-gray-200 rounded-full h-6 relative">
              <div 
                className="h-6 rounded-full"
                style={{ 
                  width: `${Math.max((item.value / maxValue) * 100, 3)}%`,
                  backgroundColor: useCustomColors && item.color ? item.color : color,
                  height: `${barHeight}px`,
                }}
              ></div>
              <span className="absolute inset-0 flex items-center pl-2 text-xs text-white font-medium">
                {item.formattedValue || item.value.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      ))}
      <div className="text-xs text-gray-500 text-right mt-2">{dataLabel}</div>
    </div>
  );
};

BarChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      formattedValue: PropTypes.string,
      color: PropTypes.string
    })
  ),
  color: PropTypes.string,
  dataLabel: PropTypes.string,
  useCustomColors: PropTypes.bool,
  barHeight: PropTypes.number,
  compact: PropTypes.bool,
  // For backward compatibility
  electorate: PropTypes.object,
  similarElectorates: PropTypes.array
};

export default BarChart;